import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { Badge, Bandeau, Carte, Champ, Modale, Squelette, Vide } from '../components/ui';
import { centimes, dateFr, deviseReference, isoJour, montant, nomClient } from '../lib/format';
import { T } from '../i18n';
import type { Vehicule } from '../api/types';

interface Amende {
  _id: string; dateInfraction: string; montantMinor: number; numeroPV?: string;
  description?: string; statut: string;
  vehicle?: { marque: string; modele: string; immatriculation: string };
  customer?: { nom: string; prenom?: string; raisonSociale?: string; telephone?: string };
  reservation?: { numero: string };
}

/**
 * L'amende arrive deux mois plus tard : le systeme retrouve seul qui conduisait
 * a la date de l'infraction (article 07).
 */
export default function Amendes() {
  const [liste, setListe] = useState<Amende[] | null>(null);
  const [vehicules, setVehicules] = useState<Vehicule[]>([]);
  const [ouvert, setOuvert] = useState(false);
  const [f, setF] = useState({ vehicle: '', date: isoJour(new Date()), montant: '', pv: '', description: '' });
  const [resultat, setResultat] = useState<{ amende: Amende; reservation: { numero: string; customer?: unknown } | null } | null>(null);
  const [erreur, setErreur] = useState('');

  const charger = useCallback(() => { api.get<Amende[]>('/operations/amendes').then(setListe); }, []);
  useEffect(() => { charger(); api.get<Vehicule[]>('/vehicules').then(setVehicules); }, [charger]);
  if (!liste) return <Squelette lignes={6} />;

  async function creer() {
    setErreur('');
    try {
      const r = await api.post<typeof resultat>('/operations/amendes', {
        vehicle: f.vehicle, dateInfraction: f.date, montantMinor: centimes(Number(f.montant || 0)),
        numeroPV: f.pv || undefined, description: f.description || undefined,
      });
      setResultat(r); setOuvert(false);
      setF({ vehicle: '', date: isoJour(new Date()), montant: '', pv: '', description: '' });
      charger();
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : T.amendes.erreur); }
  }

  const aRefacturer = liste.filter((a) => a.statut === 'a_refacturer');
  const total = aRefacturer.reduce((a, x) => a + x.montantMinor, 0);

  const ta = T.amendes;
  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{ta.titre}</h1>
          <p>{ta.sousTitre}</p>
        </div>
        <div className="actions"><button className="btn btn-p" onClick={() => setOuvert(true)}>{ta.enregistrer}</button></div>
      </div>

      {resultat && (
        <div style={{ marginBottom: 14 }}>
          <Bandeau type={resultat.reservation ? 'ok' : 'warn'}
                   titre={resultat.reservation ? ta.rattachee(resultat.reservation.numero) : ta.aucuneLocation}>
            {resultat.reservation
              ? ta.retrouve(nomClient(resultat.reservation.customer as never))
              : ta.chargeAgence}
          </Bandeau>
        </div>
      )}
      {aRefacturer.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <Bandeau type="accent" titre={ta.aRefacturer(montant(total))}>
            {ta.aRefacturerTexte(aRefacturer.length)}
          </Bandeau>
        </div>
      )}

      <Carte pad={false}>
        {liste.length === 0 ? <div className="carte-p"><Vide titre={ta.aucune} /></div> : (
          <div className="enveloppe-tbl">
            <table className="tbl">
              <thead>
                <tr><th>{ta.col.infraction}</th><th>{ta.col.vehicule}</th><th>{ta.col.conducteur}</th><th>{ta.col.pv}</th>
                    <th className="droite">{ta.col.montant}</th><th>{ta.col.statut}</th></tr>
              </thead>
              <tbody>
                {liste.map((a) => (
                  <tr key={a._id}>
                    <td className="num">{dateFr(a.dateInfraction)}<div className="mini sourdine">{a.description}</div></td>
                    <td><b>{a.vehicle?.marque} {a.vehicle?.modele}</b><div className="mini sourdine">{a.vehicle?.immatriculation}</div></td>
                    <td>{a.reservation ? <>{nomClient(a.customer)}<div className="mini sourdine">{a.reservation.numero}</div></> : <span className="sourdine mini">{ta.chargeAgenceCourt}</span>}</td>
                    <td className="mini sourdine">{a.numeroPV ?? '—'}</td>
                    <td className="droite montant">{montant(a.montantMinor)}</td>
                    <td><Badge statut={a.statut === 'a_refacturer' ? 'retour' : a.statut === 'refacturee' ? 'en_cours' : 'terminee'}
                               libelle={ta.statuts[a.statut] ?? a.statut.replace(/_/g, ' ')} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Carte>

      {ouvert && (
        <Modale titre={ta.enregistrer} onFermer={() => setOuvert(false)}>
          <p className="mini sourdine" style={{ marginBottom: 12 }}>
            {ta.aide}
          </p>
          <div style={{ display: 'grid', gap: 12 }}>
            <Champ label={ta.vehicule}>
              <select value={f.vehicle} onChange={(e) => setF({ ...f, vehicle: e.target.value })}>
                <option value="">{ta.choisir}</option>
                {vehicules.map((v) => <option key={v._id} value={v._id}>{v.marque} {v.modele} — {v.immatriculation}</option>)}
              </select>
            </Champ>
            <div className="grille g2">
              <Champ label={ta.date}><input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Champ>
              <Champ label={ta.montant(deviseReference())}><input type="number" value={f.montant} onChange={(e) => setF({ ...f, montant: e.target.value })} /></Champ>
            </div>
            <Champ label={ta.pv}><input value={f.pv} onChange={(e) => setF({ ...f, pv: e.target.value })} /></Champ>
            <Champ label={ta.description}><input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder={ta.descriptionExemple} /></Champ>
          </div>
          {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setOuvert(false)}>{ta.annuler}</button>
            <button className="btn btn-p" disabled={!f.vehicle || !f.montant} onClick={() => void creer()}>{ta.rattacher}</button>
          </div>
        </Modale>
      )}
    </>
  );
}
